/**
 * The live `/wallet` container (#579 / AD-07) — a child's money, on real data. READ ONLY.
 *
 * **Two reads for an ordinary sign-in, and the first one is not optional.** `GET /api/wallet/:userId`
 * takes a user id in its path and nothing the browser holds names the caller. So this asks
 * `GET /api/auth/verify` first, which answers `user.id` (`server/auth/routes/auth.routes.js:132`).
 * `TodayRoute.jsx` does the same, for the same reason. A verify response with no id is a FAILED READ:
 * guessing an id would ask the server for somebody else's money and be told 403, which is a worse
 * screen than an honest error. A family turn names the member instead, and verify is not asked (the
 * last paragraph).
 *
 * **The endpoint this screen must never call.** `GET /api/payday/preview` is `requireAdmin`
 * (`server/routes/payday.routes.js:51`) — a child seeing the household's eligible money is a
 * disclosure it has no reason to make, so a child gets 403. AD-07's payday-preview clause is
 * already satisfied on the PARENT surface at `ApprovalsRoute.jsx:125`. Wiring it here is the
 * obvious wrong move and `WalletRoute.test.jsx` pins that it does not happen.
 *
 * **Three sentences about money, and the page shows them apart.** `balance` is settled, `earned`
 * is approved-but-unpaid, `pending` is awaiting a parent. `Wallet.jsx`'s HONESTY RULE is that the
 * hero carries the SETTLED total alone; the other two belong to the sibling pending card.
 * `lib/walletMapping.js` owns that split and every falsifying case for it.
 *
 * **What this screen deliberately does NOT say**, because nothing it reads measures it:
 *
 * | the page would say | what exists |
 * | --- | --- |
 * | "Chapter 13 arrives Friday" | this container reads no payday window, so there is no next date |
 * | a streak flame in the header | nothing on either read counts a streak |
 * | "4 more Fridays" until the goal | `getSavingGoal` returns label, target, saved and percent — no estimate |
 *
 * S14 adds per-jar allocated amounts from separate settled-history reads. The original wallet payload
 * still supplies only the parent-set split, never a spendable jar balance.
 *
 * **A missing currency is a failed read.** `tenants.currency` is `NOT NULL DEFAULT 'USD'`
 * (`migrations/0000000000000_baseline.sql:2485`), so `null` can only mean the payload did not
 * describe a real family. Rendering the digits anyway would put a bare number where money goes.
 *
 * **The saving goal is allowed to fail on its own.** It is a second, smaller claim. A 5xx there
 * must not take down a wallet that was read successfully, so it is folded into a settled result —
 * the pattern `ApprovalsRoute.jsx:122` established.
 *
 * **The vanishing-warning trap**, the same one every live route here documents: going live leaves
 * fixture mode, which silently REMOVES `SnapshotNotice`. That is why `showSnapshotNotice` is
 * passed explicitly rather than left to its default.
 *
 * **A family turn (#580 D1, #902) names the member, and verify cannot.** During a child's turn on a
 * parent's device the session is the PARENT's, so verify's `user.id` is the parent's, and the turn
 * refuses the parent's wallet (403). The member and the turn's generation come from the actor context's `familyTurn`
 * instead (`api/session.js` `toFamilyTurn`), and a turn with no live member opens nobody's wallet. What
 * the screen holds is tagged with the turn it was read for, and only that turn's answers are drawn,
 * from the first render that names a new turn: a slow answer from an earlier turn, the same child's
 * included, never paints.
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import LanternLoader from '../components/LanternLoader.jsx';
import { ChapterPanel, JarEntriesPanel } from '../components/WalletDrillIn.jsx';
import { ApiError, api } from '../api/client.js';
import { GOAL_UNREADABLE, NOT_BUILT_BESIDE_ALLOCATIONS, toWalletView } from '../lib/walletMapping.js';
import { allocatedFor, toChapterDetail, toJarHistory } from '../lib/walletStoryMapping.js';
import { turnSubject } from '../lib/familyTurnSubject.js';
import { useOptionalActor } from '../state/ActorContext.jsx';
import { freshnessLead, useLiveRefresh } from '../state/HouseholdRealtime.jsx';

const Wallet = lazy(() => import('../pages/Wallet.jsx'));

/** Said while a read is in flight. Never a claim about an amount. */
export const LOADING_LABEL = 'Waiting for your confirmed balance and your payday story.';

/** Said when a read failed. Calm, and it never implies the money went anywhere. */
export const ERROR_LABEL = 'We couldn’t load your wallet just now — your coins are safe.';

/** Said when the payload arrived but could not be read. Distinct from a network failure. */
export const UNREADABLE = 'Your wallet came back in a shape we did not expect — your coins are safe.';

/** Said when verify answered without naming the caller. There is no id to ask about. */
export const NO_IDENTITY = 'We couldn’t tell whose wallet to open — your coins are safe.';

/**
 * Said when the saving-goal read failed, as opposed to a child who simply has no goal.
 *
 * Every rejection used to be flattened into "no goal", so a 403, a 5xx and a child with nothing
 * saved for produced the same screen (review finding #6). The wallet still renders — the goal is a
 * second, smaller claim — but the child is told which one happened.
 *
 * ONE definition, re-exported. The mapping says the same sentence for a fulfilled-but-malformed
 * body, and two copies of one sentence is how the two drift apart.
 */
export { GOAL_UNREADABLE } from '../lib/walletMapping.js';

/**
 * WHERE THE "NOT BUILT" DISCLOSURE LIVES, because the obvious answer does not exist.
 *
 * A `NOT_BUILT_NOTICE` constant used to be exported from here and passed to nothing, so the screen
 * promised a disclosure it never rendered (review finding #8). `Wallet.jsx` has **no `note` prop**
 * — unlike `Today.jsx` — and adding one to a presentational page is not this change's business.
 * The jar split note IS a rendered page-level sentence, and the jars are exactly the thing whose
 * balances this screen does not have, so `lib/walletMapping.js` appends the disclosure there.
 */

/**
 * Who am I, then what do I have. The GOAL is deliberately not part of this chain.
 *
 * It used to ride in the same `Promise.all`, folded through a `settled()` helper. `settled()`
 * converts a REJECTION and nothing else, so a goal request that simply never settled kept the
 * whole `Promise.all` pending — a wallet that had already arrived sat behind the loading screen
 * with no retry control and no way out. The goal is the smaller claim, so it is now fetched
 * separately by `loadGoal` and can never gate this.
 *
 * Each rejection is TAGGED with the read that produced it. Without that, `loadMessage` had to
 * guess, and it told a child that an expired session proved their wallet belonged to somebody
 * else.
 *
 * During a family turn (`subject`, below) verify is not asked: the turn names the member.
 */
async function loadWallet(signal, subject) {
  const me = subject
    ? subject.member
    : (await api.get('/api/auth/verify', { signal }).catch((error) => {
      if (error) error.stage = 'verify';
      throw error;
    }))?.user;

  const userId = me ? me.id : undefined;
  if (userId === undefined || userId === null || userId === '') {
    const error = new Error('verify named no user');
    error.noIdentity = true;
    throw error;
  }

  const id = encodeURIComponent(String(userId));
  const wallet = await api.get(`/api/wallet/${id}`, { signal }).catch((error) => {
    if (error) error.stage = 'wallet';
    throw error;
  });

  return { me, wallet, userId, turnKey: subject ? subject.key : null };
}

/** #902: the turn's member as this screen opens a wallet for it (`lib/familyTurnSubject.js`). */
const walletMember = (member) => ({ id: member.userId, username: member.firstName ?? '' });

/** What the screen draws while the turn on screen has no answer of its own yet. */
const NOT_YET = Object.freeze({ status: 'loading', data: null, error: null });

/**
 * #583 N1 S14 — one jar's settled allocations, a page at a time. The three first pages give each jar card
 * its ALLOCATED total, shown only when all three read cleanly; otherwise the jars keep the split-only
 * rendering and its "not built" note, which stays true. Opening a jar reads it again, so the entries are fresh.
 */
const JAR_NAMES = Object.freeze({ save: 'Save', share: 'Share', spend: 'Spend' });
const jarFailure = (name) => `We couldn’t load your ${name} entries just now — your coins are safe.`;
export const CHAPTER_FAILURE = 'We couldn’t load this payday chapter just now — your coins are safe.';
const shown = (n, name) => `Showing ${n} ${name} ${n === 1 ? 'entry' : 'entries'}.`;

function loadJar(userId, jar, cursor, signal) {
  const after = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
  return api.get(`/api/wallet/${encodeURIComponent(String(userId))}/jar-history?jar=${jar}${after}`, { signal });
}

/** The saving goal, on its own. A failure here is reported on the card, never on the wallet. */
function loadGoal(userId, signal) {
  const id = encodeURIComponent(String(userId));
  return api.get(`/api/wallet/${id}/saving-goal`, { signal });
}

/**
 * Turn a rejection into the one sentence this screen is willing to say about it.
 *
 * **A 403 only proves ownership when the WALLET refused it.** `selfOrParent`
 * (`server/routes/wallet.routes.js`) is what answers "that wallet belongs to another family
 * member". A 403 from `/api/auth/verify` is a session problem and says nothing about whose money
 * this is, so claiming otherwise states a fact the status code does not carry.
 */
function loadMessage(error) {
  if (error && error.noIdentity) return NO_IDENTITY;
  if (error instanceof ApiError && error.status === 403 && error.stage === 'wallet') {
    return 'That wallet belongs to another family member — your coins are safe.';
  }
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return 'We couldn’t confirm who you are just now — your coins are safe.';
  }
  return ERROR_LABEL;
}

export default function WalletRoute() {
  const subject = turnSubject(useOptionalActor()?.familyTurn, walletMember);
  const turnKey = subject ? subject.key : null;
  const subjectRef = useRef(subject);
  subjectRef.current = subject;
  const [stored, setState] = useState(() => ({ ...NOT_YET, turnKey }));
  // Only what was read for the turn on screen. The effect below starts the new turn's read after this
  // render has committed, so without this the first frame of a new turn drew the last one's wallet.
  const state = stored.turnKey === turnKey ? stored : NOT_YET;
  // The goal has its OWN state, which is the whole point: it resolves, fails or stays pending
  // without the wallet ever waiting on it.
  const [goalState, setGoalState] = useState({ settled: false, goal: null, failed: false });
  const [reloadKey, setReloadKey] = useState(0);
  // S14: the three jars' allocated views (all or nothing), the open drill-in, and its one status line.
  const [alloc, setAlloc] = useState(null);
  const [allocRead, setAllocRead] = useState({ data: null, failed: false });
  const [drill, setDrill] = useState(null);
  const [announce, setAnnounce] = useState('');
  const headingRef = useRef(null);
  const walletRef = useRef(null);
  const openerRef = useRef(null);
  const restoreRef = useRef(false);
  const drillSeq = useRef(0);
  const drillNow = useRef(null);
  drillNow.current = drill;
  const memberRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setState({ status: 'loading', data: null, error: null, turnKey });
    setGoalState({ settled: false, goal: null, failed: false });
    setAlloc(null);
    setDrill(null);
    drillSeq.current += 1;

    loadWallet(controller.signal, subjectRef.current)
      .then((data) => {
        if (!live) return;
        setState({ status: 'ready', data, error: null, turnKey });
      })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setState({ status: 'error', data: null, error, turnKey });
      });

    return () => { live = false; controller.abort(); };
  }, [reloadKey, turnKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  const who = state.status === 'ready' ? state.data : null;

  const openJar = useCallback((jar) => {
    if (!who) return;
    const seq = ++drillSeq.current;
    openerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    setDrill({ type: 'jar', key: `jar:${jar}`, member: who.userId, jar, status: 'loading', view: null, moreLoading: false });
    setAnnounce('');
    loadJar(who.userId, jar, null)
      .then((payload) => {
        if (seq !== drillSeq.current) return;
        const view = toJarHistory(payload, { jar, expectUserId: who.me.id });
        if (!view) throw new Error('unreadable');
        // The fresh read is the card's too, so a jar and its own open panel never show two totals.
        setAlloc((prev) => (prev ? { ...prev, [jar]: view } : prev));
        setDrill({ type: 'jar', key: `jar:${jar}`, member: who.userId, jar, status: 'ready', view, moreLoading: false });
        setAnnounce(shown(view.entries.length, JAR_NAMES[jar]));
      })
      .catch(() => {
        if (seq !== drillSeq.current) return;
        setDrill({ type: 'jar', key: `jar:${jar}`, member: who.userId, jar, status: 'failed', view: null, moreLoading: false });
        setAnnounce(jarFailure(JAR_NAMES[jar]));
      });
  }, [who]);

  const moreJar = useCallback(() => {
    const open = drill;
    if (!who || !open || open.type !== 'jar' || !open.view || !open.view.nextCursor) return;
    const seq = drillSeq.current;
    setDrill({ ...open, moreLoading: true });
    loadJar(who.userId, open.jar, open.view.nextCursor)
      .then((payload) => {
        if (seq !== drillSeq.current) return;
        const next = toJarHistory(payload, { jar: open.jar, expectUserId: who.me.id });
        if (!next) throw new Error('unreadable');
        const view = { ...next, entries: [...open.view.entries, ...next.entries] };
        setAlloc((prev) => (prev ? { ...prev, [open.jar]: view } : prev));
        setDrill({ ...open, view, moreLoading: false });
        setAnnounce(shown(view.entries.length, JAR_NAMES[open.jar]));
      })
      .catch(() => {
        if (seq !== drillSeq.current) return;
        setDrill({ ...open, moreLoading: false });
        setAnnounce(`We couldn’t load more ${JAR_NAMES[open.jar]} entries just now — your coins are safe.`);
      });
  }, [who, drill]);

  const openChapter = useCallback((payoutId) => {
    if (!who) return;
    const seq = ++drillSeq.current;
    openerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    setDrill({ type: 'chapter', key: `chapter:${payoutId}`, member: who.userId, status: 'loading', view: null });
    setAnnounce('');
    api.get(`/api/wallet/${encodeURIComponent(String(who.userId))}/chapters/${encodeURIComponent(String(payoutId))}`)
      .then((payload) => {
        if (seq !== drillSeq.current) return;
        const view = toChapterDetail(payload, { expectUserId: who.me.id, payoutId });
        if (!view) throw new Error('unreadable');
        setDrill({ type: 'chapter', key: `chapter:${payoutId}`, member: who.userId, status: 'ready', view });
        setAnnounce('Showing the payday chapter.');
      })
      .catch(() => {
        if (seq !== drillSeq.current) return;
        setDrill({ type: 'chapter', key: `chapter:${payoutId}`, member: who.userId, status: 'failed', view: null });
        setAnnounce(CHAPTER_FAILURE);
      });
  }, [who]);

  const closeDrill = useCallback(() => {
    drillSeq.current += 1;
    restoreRef.current = true;
    setDrill(null);
    setAnnounce('');
  }, []);

  useEffect(() => {
    if (!drill) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        closeDrill();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drill, closeDrill]);

  // Focus follows the drill-in: its heading when one opens, back to its opener when it closes.
  const drillKey = drill ? drill.key : null;
  useEffect(() => {
    if (drillKey) {
      if (headingRef.current) headingRef.current.focus();
    } else if (restoreRef.current) {
      restoreRef.current = false;
      const opener = openerRef.current;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    }
  }, [drillKey]);

  /**
   * #580: re-read the wallet when the household changes. The goal again rides on its own, so it
   * cannot hold the wallet up, and only a goal that ANSWERED replaces the one on screen. A wallet
   * that comes back unreadable, or for somebody else, keeps the screen as it was. An answer is
   * stored with the family turn it was READ for (#902), so one for an earlier turn is never drawn.
   */
  const live = useLiveRefresh('wallet', {
    read: (signal) => loadWallet(signal, subjectRef.current),
    apply: (data) => {
      if (!toWalletView(data.wallet, null, { expectUserId: data.me.id })) return false;
      setState({ status: 'ready', data, error: null, turnKey: data.turnKey });
      return true;
    },
    enabled: state.status === 'ready',
  });

  // Initial and live goals share one lifecycle. Retiring a read also retires its answer when
  // the client ignores AbortSignal; a different wallet can never inherit the previous goal.
  useEffect(() => {
    if (state.status !== 'ready' || !state.data) return undefined;
    const { userId } = state.data;
    const controller = new AbortController();
    setGoalState((prev) => prev.userId === userId ? prev : { userId, settled: false, goal: null, failed: false });
    const failed = (error) => {
      if (controller.signal.aborted) return;
      const refused = error instanceof ApiError && [401, 403].includes(error.status);
      setGoalState((prev) => ({ ...prev, userId, settled: true, goal: refused ? null : prev.goal, failed: true }));
    };
    loadGoal(userId, controller.signal).then((goal) => {
      if (controller.signal.aborted) return;
      if (!goal || typeof goal !== 'object') { failed(); return; }
      setGoalState({ userId, settled: true, goal, failed: false });
    }, failed);
    return () => controller.abort();
  }, [state.data, state.status]);

  // S14 allocations ride the same lifecycle: the first load and every live refresh read the three jars.
  // Only three readable answers for THIS member replace the cards. An open jar and its card receive
  // the same new first page together; older opening/pagination reads cannot overwrite that page.
  // Pending or failed batches keep the screen with a qualified freshness note.
  useEffect(() => {
    if (state.status !== 'ready' || !state.data) return undefined;
    const { userId, me } = state.data;
    if (memberRef.current !== null && memberRef.current !== userId) {
      drillSeq.current += 1;
      setDrill(null);
      if (drillNow.current) {
        restoreRef.current = false;
        const heading = walletRef.current?.querySelector('h1');
        if (heading) { heading.tabIndex = -1; heading.focus(); }
        setAnnounce('Member changed; the open wallet details have closed.');
      } else setAnnounce('');
    }
    memberRef.current = userId;
    const controller = new AbortController();
    setAllocRead({ data: state.data, failed: false });
    const failed = () => {
      if (!controller.signal.aborted) setAllocRead({ data: state.data, failed: true });
    };
    Promise.all(Object.keys(JAR_NAMES).map((jar) => loadJar(userId, jar, null, controller.signal)
      .then((payload) => toJarHistory(payload, { jar, expectUserId: me.id }))))
      .then(([save, share, spend]) => {
        if (controller.signal.aborted) return;
        if (!save || !share || !spend) { failed(); return; }
        const open = drillNow.current;
        const next = { userId, data: state.data, save, share, spend };
        if (open && open.member === userId && open.type === 'jar') {
          drillSeq.current += 1;
          setDrill({ ...open, status: 'ready', view: next[open.jar], moreLoading: false });
          setAnnounce(`${JAR_NAMES[open.jar]} entries updated. ${shown(next[open.jar].entries.length, JAR_NAMES[open.jar])}`);
        }
        setAlloc(next);
      }, failed);
    return () => controller.abort();
  }, [state.data, state.status]);

  const view = useMemo(
    () => (state.status === 'ready'
      // `expectUserId` fails the view CLOSED when the payload describes a different member.
      // `selfOrParent` already authorizes the request server-side, so this guards a stale cache,
      // a routing defect or a tenant-boundary failure — not an API hole (review finding #5).
      // A goal that has not settled yet is passed as `null`, which means "no read attempted" —
      // NOT "this child has no goal". The card simply appears when the answer arrives.
      ? toWalletView(state.data.wallet, goalState.userId === state.data.userId && goalState.settled ? goalState.goal : null,
        { expectUserId: state.data.me.id })
      : null),
    [state, goalState],
  );

  /**
   * One unreadable outcome, owned in ONE place.
   *
   * This used to add its own `view.currency == null` check, which an EMPTY STRING passed — and the
   * mapping then formatted money as bare digits, producing exactly the currency-free wallet the
   * comment above it said was refused (review finding #4). Every readability rule now lives in
   * `toWalletView`, which answers `null`, so there is no second guard to drift out of step.
   */
  const unreadable = state.status === 'ready' && view === null;

  const loadError = state.status === 'error'
    ? loadMessage(state.error)
    : unreadable ? UNREADABLE : null;

  const ready = state.status === 'ready' && !unreadable;
  const goalFailed = ready && goalState.userId === state.data.userId && goalState.failed;

  // Each jar: its allocated total beside its fill, and a button that opens its entries. Only when all three
  // jar reads came back readable; one failure keeps every jar on the split-only rendering.
  const mine = ready && alloc && alloc.userId === state.data.userId ? alloc : null;
  // Socket/wallet freshness alone cannot describe allocations from an earlier wallet read.
  const allocationsCurrent = ready && mine && mine.data === state.data;
  const allocationNote = !ready || allocationsCurrent ? ''
    : allocRead.data === state.data && allocRead.failed ? 'Jar allocations may be out of date.' : 'Jar allocations are updating.';
  const freshness = live.status === 'live' && !allocationsCurrent ? '' : freshnessLead(live.status);
  const jars = useMemo(() => {
    if (!ready) return [];
    if (!mine) return view.jars;
    return view.jars.map((jar) => {
      const allocated = allocatedFor(mine[jar.kind], view.currency);
      return {
        ...jar,
        amount: allocated ? allocated.amount : undefined,
        currency: allocated ? allocated.currency : undefined,
        percentLabel: allocated ? `Fill shows your ${jar.level}% split` : (
          <span style={{ overflowWrap: 'anywhere' }}>
            {`Fill shows your ${jar.level}% split. More than one currency. Open entries to see each amount.`}
          </span>
        ),
        onActivate: () => openJar(jar.kind),
      };
    });
  }, [ready, view, mine, openJar]);
  const jarsSplitNote = !ready ? '' : mine
    ? `Allocated from settled paydays. The fill shows your split (${view.jars.map((j) => j.level).join(' / ')}), `
      + `not a target. Spending is not tracked yet, so these are allocations, not what is left to spend. ${NOT_BUILT_BESIDE_ALLOCATIONS}`
    : view.jarsSplitNote;
  const chapters = useMemo(() => (ready ? view.chapters.map((chapter) => ({
    ...chapter, onActivate: () => openChapter(chapter.id),
  })) : []), [ready, view, openChapter]);

  // `ready` first: the view goes `null` (fail closed) when a late goal read names another member. A drill-in
  // opened for another member is never drawn beside this one's wallet, even before the effect closes it.
  const shownDrill = ready && drill && drill.member === state.data.userId ? drill : null;
  const openJarView = shownDrill && shownDrill.type === 'jar' ? shownDrill : null;
  const jarDetail = openJarView ? (
    <JarEntriesPanel
      name={JAR_NAMES[openJarView.jar]}
      label={`Allocated to ${JAR_NAMES[openJarView.jar]}`}
      level={(view.jars.find((j) => j.kind === openJarView.jar) || {}).level ?? null}
      status={openJarView.status}
      view={openJarView.view}
      failure={jarFailure(JAR_NAMES[openJarView.jar])}
      moreLoading={openJarView.moreLoading}
      onMore={moreJar}
      onClose={closeDrill}
      headingRef={headingRef}
    />
  ) : null;
  const storyDetail = ready ? (
    <>
      {shownDrill && shownDrill.type === 'chapter' ? (
        <ChapterPanel status={shownDrill.status} view={shownDrill.view} failure={CHAPTER_FAILURE} onClose={closeDrill} headingRef={headingRef} />
      ) : null}
      <p className="wlt-sr" role="status" aria-live="polite" data-testid="wallet-drill-status">{announce}</p>
    </>
  ) : null;

  return (
    <Suspense fallback={<LanternLoader label="Loading your wallet…" />}>
      <Wallet
        innerRef={walletRef}
        // Explicit, because leaving fixture mode removes the fixture banner silently.
        showSnapshotNotice={false}
        kidName={ready && state.data.me && state.data.me.username ? state.data.me.username : ''}
        // Nothing on either read counts a streak. `null`, never a number nobody measured.
        streak={null}
        balance={ready ? view.balance : null}
        currency={ready ? view.currency : undefined}
        pending={ready ? view.pending : null}
        jars={jars}
        jarsSplitNote={ready ? [jarsSplitNote, freshness, allocationNote, goalFailed ? 'Saving goal may be out of date.' : ''].filter(Boolean).join(' ') : ''}
        jarDetail={jarDetail}
        storyDetail={storyDetail}
        // A goal the SERVER refused is different from a child with no goal. `Wallet.jsx` renders a
        // goal card carrying only a `message`, which is the honest shape for "we could not read it".
        goal={ready ? (view.goal ?? (goalFailed ? { message: GOAL_UNREADABLE } : null)) : null}
        chapters={chapters}
        // This container reads no payday window, so there is no next payday to announce.
        nextChapter={null}
        loading={state.status === 'loading'}
        error={loadError}
        onRetry={reload}
        loadingLabel={LOADING_LABEL}
        errorLabel={ERROR_LABEL}
      />
    </Suspense>
  );
}
